"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface PromptSuggestionsProps {
  className?: string;
}

export default function PromptSuggestions({ className = "" }: PromptSuggestionsProps) {
  const [category, setCategory] = useState("black-friday");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const promptCategories = [
    { id: "black-friday", name: "Black Friday", icon: "🔥", color: "red" },
    { id: "viral-content", name: "Conteúdo Viral", icon: "🚀", color: "purple" },
    { id: "email-marketing", name: "Email Marketing", icon: "📧", color: "blue" },
    { id: "copywriting", name: "Copywriting", icon: "✍️", color: "green" },
    { id: "social-media", name: "Social Media", icon: "📱", color: "pink" },
    { id: "sales-funnel", name: "Funil de Vendas", icon: "🎯", color: "yellow" },
    { id: "upsell", name: "Upsell/Cross-sell", icon: "💰", color: "indigo" },
    { id: "retention", name: "Retenção", icon: "🔄", color: "cyan" }
  ];

  const generateSuggestions = async () => {
    setIsLoading(true);
    try {
      // Simular geração de sugestões (em produção, chamaria API real)
      const mockSuggestions = getSuggestionsByCategory(category);
      setSuggestions(mockSuggestions);
      addToast("💡 Sugestões de prompts geradas!", "success");
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar sugestões", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getSuggestionsByCategory = (cat: string) => {
    const suggestionsMap: Record<string, any[]> = {
      "black-friday": [
        {
          title: "Oferta Relâmpago",
          prompt: "Crie uma oferta relâmpago com 90% OFF que gere urgência máxima e escassez imediata. Inclua contador regressivo, prova social e bônus exclusivos.",
          useCase: "Lançamentos de produtos",
          conversion: "Alta"
        },
        {
          title: "Venda Flash 24h",
          prompt: "Desenvolva uma campanha de venda flash de 24 horas com gatilhos de FOMO, escassez de estoque limitado e prova social explosiva.",
          useCase: "Limpeza de estoque",
          conversion: "Muito Alta"
        },
        {
          title: "Black Friday Antecipada",
          prompt: "Crie campanha de Black Friday antecipada com exclusividade para membros VIP, descontos progressivos e senso de urgência.",
          useCase: "Atração de clientes",
          conversion: "Altíssima"
        }
      ],
      "viral-content": [
        {
          title: "Hook de 3 Segundos",
          prompt: "Crie um hook viral para os primeiros 3 segundos que gere curiosidade insaciável e impossibilidade de não continuar assistindo.",
          useCase: "Reels/TikTok",
          conversion: "Alta"
        },
        {
          title: "Storytelling Emocional",
          prompt: "Desenvolva storytelling emocional poderoso que conecte com a dor do público e apresente solução de forma hipnótica.",
          useCase: "Vídeos longos",
          conversion: "Muito Alta"
        },
        {
          title: "Controvérsia Controlada",
          prompt: "Crie conteúdo controverso que gere debate massivo sem ofender, usando polarização estratégica para engajamento.",
          useCase: "Posts virais",
          conversion: "Altíssima"
        }
      ],
      "email-marketing": [
        {
          title: "Assunto Irresistível",
          prompt: "Escreva assunto de email com taxa de abertura garantida usando curiosidade, urgência e personalização.",
          useCase: "Campanhas de email",
          conversion: "Média"
        },
        {
          title: "Sequência de Abandono",
          prompt: "Crie sequência de 5 emails para recuperação de carrinho abandonado com gatilhos emocionais e ofertas progressivas.",
          useCase: "Recuperação de vendas",
          conversion: "Alta"
        }
      ]
    };
    
    return suggestionsMap[cat] || [];
  };

  const copyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    addToast("📋 Prompt copiado!", "success");
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      red: "bg-red-900/20 border-red-500/30 text-red-400",
      purple: "bg-purple-900/20 border-purple-500/30 text-purple-400",
      blue: "bg-blue-900/20 border-blue-500/30 text-blue-400",
      green: "bg-green-900/20 border-green-500/30 text-green-400",
      pink: "bg-pink-900/20 border-pink-500/30 text-pink-400",
      yellow: "bg-yellow-900/20 border-yellow-500/30 text-yellow-400",
      indigo: "bg-indigo-900/20 border-indigo-500/30 text-indigo-400",
      cyan: "bg-cyan-900/20 border-cyan-500/30 text-cyan-400"
    };
    return colorMap[color] || "bg-gray-900/20 border-gray-500/30 text-gray-400";
  };

  const currentCategory = promptCategories.find(cat => cat.id === category);
  const colorClasses = getColorClasses(currentCategory?.color || "blue");

  return (
    <div className={`bg-gray-900 text-white rounded-lg p-6 border border-gray-700 ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-white">
          💡 PROMPT SUGGESTION ENGINE 💡
        </h3>
        <div className="text-xs text-gray-400 mb-4">
          [PROMPT_AI] v1.0.0 | CATEGORIES: {promptCategories.length} | STATUS: ACTIVE
        </div>
      </div>
      
      <div className="mb-6">
        <label className="block text-xs text-gray-400 mb-3">SELECT_CATEGORY:</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {promptCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`p-3 rounded border transition-all duration-300 ${
                category === cat.id
                  ? `${getColorClasses(cat.color)} border-opacity-100`
                  : 'bg-gray-800 border-gray-600 hover:bg-gray-700'
              }`}
            >
              <div className="text-2xl mb-1">{cat.icon}</div>
              <div className="text-xs font-medium">{cat.name}</div>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={generateSuggestions}
        disabled={isLoading}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded font-bold hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> GENERATING_SUGGESTIONS...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            🚀 [GENERATE_PROMPTS] 🚀
          </span>
        )}
      </button>

      {suggestions.length > 0 && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-gray-400 mb-4">
            [PROMPTS_GENERATED] | COUNT: {suggestions.length} | CATEGORY: {currentCategory?.name}
          </div>
          
          {suggestions.map((suggestion, index) => (
            <div key={index} className={`${colorClasses} rounded p-4`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-lg font-bold mb-1">
                    {index + 1}. {suggestion.title}
                  </h4>
                  <div className="flex gap-2 text-xs">
                    <span className="bg-white/10 px-2 py-1 rounded">
                      🎯 {suggestion.useCase}
                    </span>
                    <span className="bg-white/10 px-2 py-1 rounded">
                      📈 {suggestion.conversion}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => copyPrompt(suggestion.prompt)}
                  className="text-xs bg-white/10 px-2 py-1 rounded hover:bg-white/20"
                >
                  [COPY]
                </button>
              </div>
              
              <div className="mb-3">
                <h5 className="text-sm font-bold mb-2">PROMPT_TEMPLATE:</h5>
                <div className="bg-black/30 p-3 rounded text-sm font-mono leading-relaxed">
                  {suggestion.prompt}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <button
                  onClick={() => copyPrompt(suggestion.prompt)}
                  className="bg-white/10 px-3 py-2 rounded hover:bg-white/20"
                >
                  📋 COPY_PROMPT
                </button>
                <button className="bg-white/10 px-3 py-2 rounded hover:bg-white/20">
                  ⚙️ CUSTOMIZE
                </button>
                <button className="bg-white/10 px-3 py-2 rounded hover:bg-white/20">
                  ⭐ SAVE_FAVORITE
                </button>
              </div>
            </div>
          ))}

          <div className="mt-6 text-center">
            <button className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded font-bold hover:from-green-700 hover:to-blue-700">
              🔄 [GENERATE_MORE] 🔄
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
